import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import {addCompanyData, authCompanyData} from '../../redux/actions';
import {BASE_URL_WEB} from '../../utility/constants';
import {AxiosHelperPortal} from '../../utility/helperFunctions';
import './BranchRecovery.css';

function getBranchLocation(branch) {
  return branch?.isVirtual
    ? 'Virtual'
    : [branch?.street, branch?.city, branch?.state, branch?.zip].filter(Boolean).join(', ');
}

export default function BranchRecovery({companyData}) {
  const dispatch = useDispatch();
  const [branchId, setBranchId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const recovery = companyData?.branchRecovery;
  if (!recovery) return null;

  const branches = (recovery.branches || [])
    .filter(
      branch =>
        !branch.deletedAt &&
        branch.status !== 'deleted' &&
        !branch.branchDeleted &&
        !branch.readOnly
    )
    .sort((a, b) => a.name.localeCompare(b.name));
  const selectedBranch = branches.find(branch => branch.id === branchId);
  const selectedLocation = getBranchLocation(selectedBranch);

  const connect = async event => {
    event.preventDefault();
    if (loading || !branches.some(branch => branch.id === branchId)) return;
    setLoading(true);
    setError('');
    try {
      const connection = await AxiosHelperPortal(`${BASE_URL_WEB}connect-company?`, 'post', {
        memberId: companyData.member.id,
        branchId,
      });
      if (connection?.status !== 200 || connection?.data?.success === false) throw new Error();
      // Record the explicit selection so a company default cannot replace it.
      const response = await AxiosHelperPortal(`${BASE_URL_WEB}member-active-branch?`, 'post', {
        companyId: recovery.companyId,
        branchId,
      });
      if (response?.status !== 200) throw new Error();
      dispatch(addCompanyData(response.data));
      dispatch(authCompanyData());
    } catch (failure) {
      setError(
        failure?.response?.data?.message || 'Unable to connect to this branch. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid mt-3">
      <div
        className="alert alert-info branch-recovery__panel"
        role="region"
        aria-label="Select another branch"
      >
        <p>The branch you were attached to is no longer active. Please select another branch:</p>
        {branches.length ? (
          <form onSubmit={connect}>
            <label htmlFor="replacement-branch">Branch</label>
            <div className="branch-recovery__controls">
              <select
                id="replacement-branch"
                className="form-control branch-recovery__select"
                value={branchId}
                disabled={loading}
                aria-describedby={selectedLocation ? 'replacement-branch-location' : undefined}
                onChange={event => setBranchId(event.target.value)}
              >
                <option value="">Select a branch</option>
                {branches.map(branch => (
                  <option key={branch.id} value={branch.id}>
                    {[branch.name, getBranchLocation(branch)].filter(Boolean).join(' — ')}
                  </option>
                ))}
              </select>
              <button
                type="submit"
                className="btn btn-primary branch-recovery__connect"
                disabled={loading || !branchId}
              >
                {loading ? 'Connecting…' : 'Connect to branch'}
              </button>
            </div>
            {selectedLocation && (
              <small
                id="replacement-branch-location"
                className="d-block mt-1 branch-recovery__location"
              >
                {selectedLocation}
              </small>
            )}
          </form>
        ) : (
          <p className="mb-0">
            No other branches are available. Please contact the business. Your historical records
            are still available.
          </p>
        )}
        {error && (
          <p role="alert" className="text-danger mt-2 mb-0">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
